import {
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  Paid as PaidIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns-jalali";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchSettlements,
  fetchWallet,
  requestSettlement,
} from "../../../application/actions/financialActions";
import {
  selectSettlementsData,
  selectSettlementsLoading,
  selectWalletData,
} from "../../../application/selectors/financial";

const SettlementRequest = () => {
  const dispatch = useDispatch();

  // Redux State
  const settlements = useSelector(selectSettlementsData);
  const loading = useSelector(selectSettlementsLoading);
  const wallet = useSelector(selectWalletData);

  // Local State
  const [requestDialog, setRequestDialog] = useState(false);
  const [formData, setFormData] = useState({
    amount: "",
    bankAccountNumber: "",
    iban: "",
    accountHolderName: "",
    description: "",
  });

  useEffect(() => {
    dispatch(fetchSettlements());
    dispatch(fetchWallet());
  }, [dispatch]);

  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmitRequest = async () => {
    // Validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("لطفاً مبلغ معتبر وارد کنید");
      return;
    }

    if (!formData.bankAccountNumber) {
      toast.error("لطفاً شماره حساب را وارد کنید");
      return;
    }

    if (!formData.accountHolderName) {
      toast.error("لطفاً نام صاحب حساب را وارد کنید");
      return;
    }

    const availableBalance =
      (wallet?.balance || 0) - (wallet?.onHoldAmount || 0);

    if (parseFloat(formData.amount) > availableBalance) {
      toast.error("موجودی قابل برداشت کافی نیست");
      return;
    }

    await dispatch(requestSettlement(formData));

    // Reset form and close dialog
    setFormData({
      amount: "",
      bankAccountNumber: "",
      iban: "",
      accountHolderName: "",
      description: "",
    });
    setRequestDialog(false);

    // Refresh data
    dispatch(fetchSettlements());
    dispatch(fetchWallet());
  };

  const getSettlementStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Approved":
        return "info";
      case "Completed":
        return "success";
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getSettlementStatusLabel = (status) => {
    const labels = {
      Pending: "در انتظار تأیید",
      Approved: "تأیید شده",
      Completed: "پرداخت شده",
      Rejected: "رد شده",
    };
    return labels[status] || status;
  };

  const getSettlementStatusIcon = (status) => {
    switch (status) {
      case "Pending":
        return <PendingIcon />;
      case "Approved":
        return <ApproveIcon />;
      case "Completed":
        return <PaidIcon />;
      case "Rejected":
        return <RejectIcon />;
      default:
        return <PendingIcon />;
    }
  };

  const availableBalance = (wallet?.balance || 0) - (wallet?.onHoldAmount || 0);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            تسویه حساب
          </Typography>
          <Typography variant="body1" color="text.secondary">
            درخواست و مدیریت تسویه‌های مالی
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => setRequestDialog(true)}
        >
          درخواست تسویه جدید
        </Button>
      </Box>

      {/* Available Balance Card */}
      <Card
        sx={{
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            موجودی قابل تسویه
          </Typography>
          <Typography variant="h3" fontWeight="bold">
            {availableBalance.toLocaleString("fa-IR")} ریال
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            موجودی کل: {wallet?.balance?.toLocaleString("fa-IR")} ریال | در
            انتظار: {wallet?.onHoldAmount?.toLocaleString("fa-IR")} ریال
          </Typography>
        </CardContent>
      </Card>

      {/* Settlements List */}
      <Paper>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="h6">درخواست‌های تسویه</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : settlements && settlements.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>تاریخ درخواست</strong>
                  </TableCell>
                  <TableCell>
                    <strong>مبلغ</strong>
                  </TableCell>
                  <TableCell>
                    <strong>شماره حساب</strong>
                  </TableCell>
                  <TableCell>
                    <strong>وضعیت</strong>
                  </TableCell>
                  <TableCell>
                    <strong>توضیحات</strong>
                  </TableCell>
                  <TableCell>
                    <strong>تاریخ پرداخت</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settlements.map((settlement) => (
                  <TableRow key={settlement.id}>
                    <TableCell>
                      {settlement.createdAt &&
                        format(
                          new Date(settlement.createdAt),
                          "yyyy/MM/dd HH:mm"
                        )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">
                        {settlement.amount?.toLocaleString("fa-IR")} ریال
                      </Typography>
                    </TableCell>
                    <TableCell>{settlement.bankAccountNumber}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getSettlementStatusIcon(settlement.status)}
                        label={getSettlementStatusLabel(settlement.status)}
                        color={getSettlementStatusColor(settlement.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {settlement.description || "-"}
                      {settlement.status === "Rejected" &&
                        settlement.rejectionReason && (
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ mt: 0.5 }}
                          >
                            دلیل رد: {settlement.rejectionReason}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell>
                      {settlement.completedAt
                        ? format(new Date(settlement.completedAt), "yyyy/MM/dd")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              درخواست تسویه‌ای ثبت نشده است
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setRequestDialog(true)}
              sx={{ mt: 2 }}
            >
              ثبت درخواست اول
            </Button>
          </Box>
        )}
      </Paper>

      {/* Request Dialog */}
      <Dialog
        open={requestDialog}
        onClose={() => setRequestDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>درخواست تسویه حساب</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              موجودی قابل برداشت:{" "}
              <strong>{availableBalance.toLocaleString("fa-IR")} ریال</strong>
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="مبلغ درخواستی (ریال)"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleFormChange("amount", e.target.value)}
                  helperText={`حداکثر: ${availableBalance.toLocaleString(
                    "fa-IR"
                  )} ریال`}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="شماره حساب"
                  value={formData.bankAccountNumber}
                  onChange={(e) =>
                    handleFormChange("bankAccountNumber", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="شماره شبا (اختیاری)"
                  value={formData.iban}
                  onChange={(e) => handleFormChange("iban", e.target.value)}
                  placeholder="IR"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="نام صاحب حساب"
                  value={formData.accountHolderName}
                  onChange={(e) =>
                    handleFormChange("accountHolderName", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="توضیحات (اختیاری)"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialog(false)}>انصراف</Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={!formData.amount || !formData.bankAccountNumber}
          >
            ثبت درخواست
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettlementRequest;
